import { useEffect } from "react";

import type { ConfigSchema, FieldDef } from "../lib/api";
import { cn } from "../lib/cn";
import { FileDropzone } from "./FileDropzone";

// SourceFormValues mirrors the shape POST /api/v1/sources expects in its
// "config" + "secrets" maps. The form keeps them separate so we can submit
// secrets only when the operator actually typed something new.
export interface SourceFormValues {
  config: Record<string, unknown>;
  secrets: Record<string, string>;
}

interface SourceFormProps {
  schema: ConfigSchema;
  value: SourceFormValues;
  onChange: (next: SourceFormValues) => void;
  // connectorId is required so FileDropzone can scope its server-side
  // accept-list validation to the right connector schema.
  connectorId: string;
  // editing=true relaxes required-field validation on secret fields, since the
  // server retains the existing ciphertext when the operator leaves the field
  // blank during an update.
  editing?: boolean;
}

// SourceForm renders one input per FieldDef and emits the merged values via
// onChange. The visual style is plain Tailwind — Radix primitives ride on top
// in the modal wrapper, not here.
export function SourceForm({ schema, value, onChange, connectorId, editing }: SourceFormProps) {
  const setConfigField = (key: string, next: unknown) => {
    onChange({ ...value, config: { ...value.config, [key]: next } });
  };
  const setSecretField = (key: string, next: string) => {
    onChange({ ...value, secrets: { ...value.secrets, [key]: next } });
  };

  useEffect(() => {
    const visible = new Set(
      schema.fields.filter((f) => isFieldVisible(f, value.config)).map((f) => f.key),
    );
    const schemaKeys = new Set(schema.fields.map((f) => f.key));
    let changed = false;
    const config: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value.config)) {
      if (schemaKeys.has(k) && !visible.has(k)) {
        changed = true;
        continue;
      }
      config[k] = v;
    }
    const secrets: Record<string, string> = {};
    for (const [k, v] of Object.entries(value.secrets)) {
      if (schemaKeys.has(k) && !visible.has(k)) {
        changed = true;
        continue;
      }
      secrets[k] = v;
    }
    if (changed) {
      onChange({ config, secrets });
    }
  }, [schema, value.config, value.secrets, onChange]);

  // Group consecutive inline fields into flex rows; all others stand alone.
  const visibleFields = schema.fields.filter((f) => isFieldVisible(f, value.config));
  const rows: Array<typeof visibleFields> = [];
  for (const f of visibleFields) {
    if (f.inline && rows.length > 0 && rows[rows.length - 1][0]?.inline) {
      rows[rows.length - 1].push(f);
    } else {
      rows.push([f]);
    }
  }

  return (
    <div className="space-y-4">
      {rows.map((group) =>
        group.length === 1 ? (
          <FieldRow
            key={group[0].key}
            def={group[0]}
            value={
              group[0].type === "secret"
                ? (value.secrets[group[0].key] ?? "")
                : value.config[group[0].key]
            }
            editing={editing}
            connectorId={connectorId}
            onChange={(next) =>
              group[0].type === "secret"
                ? setSecretField(group[0].key, String(next ?? ""))
                : setConfigField(group[0].key, next)
            }
          />
        ) : (
          <div key={group.map((f) => f.key).join("-")} className="flex gap-3">
            {group.map((f) => (
              <div key={f.key} className="flex-1 min-w-0">
                <FieldRow
                  def={f}
                  value={f.type === "secret" ? (value.secrets[f.key] ?? "") : value.config[f.key]}
                  editing={editing}
                  connectorId={connectorId}
                  onChange={(next) =>
                    f.type === "secret"
                      ? setSecretField(f.key, String(next ?? ""))
                      : setConfigField(f.key, next)
                  }
                />
              </div>
            ))}
          </div>
        ),
      )}
    </div>
  );
}

interface FieldRowProps {
  def: FieldDef;
  value: unknown;
  editing?: boolean;
  connectorId: string;
  onChange: (next: unknown) => void;
}

function FieldRow({ def, value, editing, connectorId, onChange }: FieldRowProps) {
  // Apply default once on mount when the form is fresh.
  useEffect(() => {
    if (def.default !== undefined && value === undefined) {
      onChange(def.default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Omit the label row entirely when the schema field has no label text.
  // Used by the ingestion-mode selector which lets the tab buttons speak for themselves.
  const label = def.label ? (
    <span className="mb-1.5 flex items-baseline justify-between">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
        {def.label}
        {def.required ? <span className="ml-1 text-rose-700">*</span> : null}
      </span>
      {def.type === "secret" && editing ? (
        <span className="text-[10.5px] italic text-ash">leave blank to keep existing</span>
      ) : null}
    </span>
  ) : null;

  const help = def.help ? (
    <span className="mt-1.5 block text-[11.5px] text-ash">{def.help}</span>
  ) : null;

  const input = (() => {
    switch (def.type) {
      case "bool":
        return (
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 rounded border-rule text-brand-600"
            />
            <span className="text-[13px] text-ink">Yes</span>
          </label>
        );
      case "int":
        return (
          <input
            type="number"
            value={value === undefined || value === null ? "" : Number(value)}
            placeholder={def.placeholder}
            required={def.required}
            onChange={(e) =>
              e.target.value === "" ? onChange(undefined) : onChange(Number(e.target.value))
            }
            className="input"
          />
        );
      case "secret":
        return (
          <input
            type="password"
            value={typeof value === "string" ? value : ""}
            placeholder={editing ? "•••••••• (unchanged)" : def.placeholder}
            required={def.required && !editing}
            autoComplete="new-password"
            onChange={(e) => onChange(e.target.value)}
            className="input font-mono"
          />
        );
      case "enum":
        return (
          <select
            value={typeof value === "string" ? value : ""}
            required={def.required}
            onChange={(e) => onChange(e.target.value)}
            className="input"
          >
            <option value="" disabled>
              Select…
            </option>
            {def.options?.map((opt) => (
              <option key={opt.value} value={opt.value} title={opt.description ?? opt.subtitle}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case "radio-group": {
        const selected = typeof value === "string" ? value : (def.options?.[0]?.value ?? "");
        const activeOption = def.options?.find((o) => o.value === selected);
        return (
          <div className="space-y-3">
            {/* Tab buttons */}
            <div className="flex w-full overflow-hidden rounded border border-rule">
              {def.options?.map((opt, i) => {
                const active = selected === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => !active && onChange(opt.value)}
                    className={cn(
                      "flex-1 py-2.5 px-4 text-left transition-colors select-none",
                      i > 0 && "border-l border-rule",
                      active
                        ? "bg-litmus-dark-green-700 text-white cursor-default"
                        : "bg-surface text-ash opacity-50 cursor-pointer hover:opacity-75",
                    )}
                  >
                    <div className="text-[12.5px] font-semibold leading-tight">{opt.label}</div>
                    {opt.subtitle ? (
                      <div
                        className={cn(
                          "mt-0.5 text-[11px] leading-tight",
                          active ? "text-white/70" : "text-ash",
                        )}
                      >
                        {opt.subtitle}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* Description paragraph for the active option */}
            {activeOption?.description ? (
              <p className="text-[12.5px] leading-relaxed text-ash" onClick={(e) => e.stopPropagation()}>{activeOption.description}</p>
            ) : null}
          </div>
        );
      }
      case "time":
        return (
          <input
            type="time"
            value={typeof value === "string" ? value : "02:00"}
            required={def.required}
            onChange={(e) => onChange(e.target.value)}
            className="input"
          />
        );
      case "textarea":
        return (
          <textarea
            value={typeof value === "string" ? value : ""}
            placeholder={def.placeholder}
            required={def.required}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            className="input"
          />
        );
      case "file":
        return (
          <FileDropzone
            def={def}
            value={typeof value === "string" ? value : ""}
            onChange={(uploadId) => onChange(uploadId)}
            connectorId={connectorId}
          />
        );
      case "string":
      default:
        return (
          <input
            type="text"
            value={typeof value === "string" ? value : ""}
            placeholder={def.placeholder}
            required={def.required}
            onChange={(e) => onChange(e.target.value)}
            className="input"
          />
        );
    }
  })();

  const Wrapper = "div";
  return (
    <Wrapper className="block">
      {label}
      {input}
      {help}
    </Wrapper>
  );
}

// emptyValuesFromSchema returns a fresh SourceFormValues with default values
// applied. Used by the AddSourceDialog when the operator picks a connector.
export function emptyValuesFromSchema(schema: ConfigSchema): SourceFormValues {
  const config: Record<string, unknown> = {};
  for (const f of schema.fields) {
    if (f.type === "secret") continue;
    if (f.visibleWhen) continue;
    if (f.default !== undefined) {
      config[f.key] = f.default;
    }
  }
  return { config, secrets: {} };
}

// Use the schema-aware setter type elsewhere too.
export type { SourceFormValues as Values };

// getMissingRequiredFields returns labels of visible required fields that have no value.
// Use the returned array to show specific "Required: X, Y" messages instead of a generic error.
export function getMissingRequiredFields(
  schema: ConfigSchema,
  values: SourceFormValues,
  editing: boolean,
): string[] {
  const missing: string[] = [];
  for (const f of schema.fields) {
    if (!isFieldVisible(f, values.config)) continue;
    if (!f.required) continue;
    if (f.type === "secret") {
      if (editing) continue;
      if (!values.secrets[f.key]) missing.push(f.label || f.key);
      continue;
    }
    const v = values.config[f.key];
    if ((v === undefined || v === null) && f.default !== undefined && f.default !== "") continue;
    if (v === undefined || v === null || v === "") missing.push(f.label || f.key);
  }
  return missing;
}

export function isSchemaSatisfied(
  schema: ConfigSchema,
  values: SourceFormValues,
  editing: boolean,
): boolean {
  return getMissingRequiredFields(schema, values, editing).length === 0;
}

function isFieldVisible(def: FieldDef, config: Record<string, unknown>): boolean {
  if (!def.visibleWhen) return true;
  return config[def.visibleWhen.field] === def.visibleWhen.equals;
}
